import { formatDateTimeForGraphLabel } from "@src/cloud-connect/utils/format-date-time-for-graph-label";
import { formatChartTooltipDate } from "@src/cloud-connect/utils/timezone-display-utils";
import { buildTooltipDateHeader } from "@src/cloud-connect/charts/chart-tooltip-utils";
import store from "@src/reducers/store";
import type { ChartThemeColors } from "../use-theme-colors-for-charts";
import {
  MULTI_LINE_DEFAULT_COLORS,
  calculateMultiLineInterval,
  withUniqueNames,
} from "../multi-line/build-multi-line-option";
import { ShotDataPointSeries } from "./extract-shot-data-series";
import { parseManualBounds } from "../axis-bounds";

export interface BuildShotDataOptionArgs {
  series: ShotDataPointSeries[];
  /** Only read when the x-axis is the timestamp. */
  times: string[];
  /** x = the event number (1..n) instead of `occurred_at`. */
  useEventIndex: boolean;
  unitLabel?: string;
  /** Manual Y bounds (engineering units). Applied only when both are
   *  present, finite and ordered; otherwise the automatic range is used. */
  yMin?: number | null;
  yMax?: number | null;
  /** Static/read-only rendering (picker preview or unbound widget). */
  static?: boolean;
  themeColors?: ChartThemeColors;
}

/** Tooltip header: the event's timestamp, and the event number when the axis
 *  shows numbers. The timestamp is ALWAYS shown — with thousands of events the
 *  axis carries only a sample of them, so a point's exact time is only
 *  readable here. `dataIndex` is what finds the event: in index mode the axis
 *  value is the number, not the time. */
const headerFor = (
  params: any,
  times: string[],
  useEventIndex: boolean,
): string => {
  const first = Array.isArray(params) ? params[0] : params;
  const dataIndex = typeof first?.dataIndex === "number" ? first.dataIndex : -1;
  const stamp = dataIndex >= 0 ? times[dataIndex] : first?.axisValue;
  const { timezone, language } = store.getState().yourSlice;
  const timeLabel =
    stamp == null ? "" : formatChartTooltipDate(stamp, timezone, language);
  return useEventIndex && first?.axisValue != null
    ? `#${first.axisValue} · ${timeLabel}`
    : timeLabel;
};

/** Line chart of the chosen JSON fields, one point per event.
 *  y = the values, x = the event number or `occurred_at`. */
export const buildShotDataLineOption = ({
  series,
  times,
  useEventIndex,
  unitLabel = "",
  yMin,
  yMax,
  static: isStatic = false,
  themeColors,
}: BuildShotDataOptionArgs): any => {
  if (series.length === 0) {
    return { series: [] };
  }
  // ECharts drops series whose names collide (legend + data), so repeats get a
  // numeric suffix. Shared with the multi-line chart, which has the same need.
  const named = withUniqueNames(series, false, false);
  const primaryText = themeColors?.primary ?? "#e6f1f2";
  const secondaryText = themeColors?.secondary ?? "#697477";
  const manual = parseManualBounds(yMin, yMax);
  const { yAxisMin, yAxisMax } = manual
    ? { yAxisMin: manual.min, yAxisMax: manual.max }
    : calculateMultiLineInterval(named.map((s) => s.values));

  return {
    grid: { left: 8, right: 28, top: 32, bottom: 8, containLabel: true },
    legend: {
      show: true,
      selectedMode: isStatic ? false : true,
      top: 0,
      left: 8,
      itemWidth: 16,
      itemHeight: 12,
      padding: [2, 8, 2, 0],
      textStyle: {
        color: primaryText,
        fontSize: 9,
        overflow: "truncate",
        width: 140,
      },
    },
    xAxis: {
      type: "category",
      // Event numbers are 1-based, so the first point reads "1", not "0".
      data: useEventIndex ? times.map((_time, index) => index + 1) : times,
      boundaryGap: false,
      axisLabel: {
        color: secondaryText,
        formatter: useEventIndex ? undefined : formatDateTimeForGraphLabel,
        fontSize: 9,
        margin: 10,
      },
    },
    yAxis: {
      type: "value",
      min: yAxisMin,
      max: yAxisMax,
      scale: true,
      axisLine: { onZero: true },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
      axisLabel: {
        color: secondaryText,
        width: 44,
        align: "right",
        overflow: "truncate",
      },
    },
    tooltip: {
      show: !isStatic,
      trigger: "axis",
      triggerOn: !isStatic ? "mousemove|click" : "none",
      confine: true,
      backgroundColor: "rgba(19, 27, 32, 0.5)",
      borderColor: "rgba(230, 241, 242, 0.2)",
      borderWidth: 1,
      textStyle: { color: primaryText, fontFamily: "Inter", fontSize: 11 },
      formatter: (params: any) => {
        const paramsArr: any[] = Array.isArray(params) ? params : [params];
        let html = `<div style="max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${buildTooltipDateHeader(
          headerFor(params, times, useEventIndex),
        )}</div>`;
        // One row per series, in series order. Only the series' own param is
        // trustworthy: the params array omits legend-hidden rows, so an index
        // into it does not line up with this list.
        named.forEach((s) => {
          const p = paramsArr.find((x) => x.seriesName === s.name);
          if (!p) return;
          const color =
            p.color ?? s.color ?? MULTI_LINE_DEFAULT_COLORS[0] ?? "#027680";
          const valueText = p.data != null ? p.data : "—";
          html += `<div style="display: flex; gap: 5px; align-items: center; color: ${primaryText}; font-weight: 100;">
            <span style="color: ${color};">●</span>
            <span style="display: inline-block; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle;">${
              s.name
            }</span>
            <span style="margin-left: auto; font-weight: 800;">${valueText}${
            unitLabel ? ` ${unitLabel}` : ""
          }</span>
          </div>`;
        });
        return html;
      },
      extraCssText:
        "backdrop-filter: blur(5px); padding: 8px 12px; box-sizing: border-box;",
    },
    series: named.map((s, idx) => {
      const color = s.color ?? MULTI_LINE_DEFAULT_COLORS[idx] ?? "#027680";
      return {
        name: s.name,
        type: "line",
        data: s.values,
        smooth: false,
        lineStyle: { color, width: 2 },
        symbol: "circle",
        symbolSize: 6,
        showSymbol: s.values.length === 1,
        itemStyle: { color },
        // A field missing from an event is a gap, not the end of the line.
        connectNulls: true,
        animation: true,
        silent: isStatic,
      };
    }),
  };
};
